/**
 * 可拖拽指令（修复input无法输入问题）
 */
export default {
  inserted(el, binding, vnode) {
    // 获取配置参数
    const config = binding.value || {};
    
    // 默认配置
    const defaults = {
      boundary: true,      // 是否限制边界
      zIndex: 9999,       // 拖拽时的z-index
      cursor: 'move',     // 鼠标指针样式
      disabled: false,    // 是否禁用拖拽
      x: 0,               // 初始X位置
      y: 0,               // 初始Y位置
      handle: null        // 拖拽手柄选择器
    };
    
    // 合并配置
    const finalConfig = { ...defaults, ...config };
    
    // 设置基本样式
    el.style.position = 'fixed';
    
    // 设置初始位置
    if (finalConfig.x !== 0 || finalConfig.y !== 0) {
      el.style.left = finalConfig.x + 'px';
      el.style.top = finalConfig.y + 'px';
    }
    
    // 获取拖拽手柄元素
    let dragHandle = el;
    if (finalConfig.handle) {
      const handleElement = el.querySelector(finalConfig.handle);
      if (handleElement) {
        dragHandle = handleElement;
      } else {
        console.warn(`拖拽手柄选择器 "${finalConfig.handle}" 未找到，使用元素本身作为手柄`);
      }
    }
    
    // 只在拖拽手柄上设置样式
    dragHandle.style.cursor = finalConfig.cursor;
    dragHandle.style.userSelect = 'none';
    
    // 拖拽状态变量
    let isDragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    
    // 检查元素是否是输入元素或可编辑元素
    function isInputOrEditableElement(element) {
      if (!element) return false;
      
      const tagName = element.tagName.toLowerCase();
      const isInput = ['input', 'textarea', 'select', 'button'].includes(tagName);
      const isContentEditable = element.isContentEditable;
      const hasEditableRole = element.getAttribute('role') === 'textbox';
      
      return isInput || isContentEditable || hasEditableRole;
    }
    
    // 开始拖拽
    function onMouseDown(e) {
      // 判断是否禁用拖拽
      if (finalConfig.disabled) return;
      
      // 检查是否点击了拖拽手柄
      if (!dragHandle.contains(e.target)) {
        return; // 不是拖拽手柄，允许正常操作
      }
      
      // 如果点击的是输入元素或可编辑元素，不触发拖拽
      if (isInputOrEditableElement(e.target)) {
        return;
      }
      
      isDragging = true;
      
      // 计算鼠标在元素内的偏移
      const rect = el.getBoundingClientRect();
      dragOffsetX = e.clientX - rect.left;
      dragOffsetY = e.clientY - rect.top;
      
      // 拖拽时提升层级
      el.style.zIndex = finalConfig.zIndex;
      
      // 只在拖拽手柄上阻止默认行为和冒泡
      e.preventDefault();
      e.stopPropagation();
    }
    
    // 拖拽移动
    function onMouseMove(e) {
      if (!isDragging) return;
      
      // 计算新位置
      let newX = e.clientX - dragOffsetX;
      let newY = e.clientY - dragOffsetY;
      
      // 边界限制
      if (finalConfig.boundary) {
        const maxX = window.innerWidth - el.offsetWidth;
        const maxY = window.innerHeight - el.offsetHeight;
        
        newX = Math.max(0, Math.min(newX, maxX));
        newY = Math.max(0, Math.min(newY, maxY));
      }
      
      // 更新位置
      el.style.left = newX + 'px';
      el.style.top = newY + 'px';
      
      // 触发自定义事件（可选）
      const event = new CustomEvent('drag-move', {
        detail: { x: newX, y: newY }
      });
      el.dispatchEvent(event);
    }
    
    // 结束拖拽
    function onMouseUp() {
      if (!isDragging) return;
      
      isDragging = false;
      el.style.zIndex = '';
      
      // 触发自定义事件（可选）
      const rect = el.getBoundingClientRect();
      const event = new CustomEvent('drag-end', {
        detail: { x: rect.left, y: rect.top }
      });
      el.dispatchEvent(event);
    }
    
    // 添加事件监听 - 只在拖拽手柄上监听mousedown
    dragHandle.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    
    // 触摸事件支持（移动端）
    dragHandle.addEventListener('touchstart', function(e) {
      if (finalConfig.disabled) return;
      
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const targetElement = document.elementFromPoint(touch.clientX, touch.clientY);
        
        // 检查是否点击了拖拽手柄
        if (!dragHandle.contains(targetElement)) return;
        
        // 如果点击的是输入元素或可编辑元素，不触发拖拽
        if (isInputOrEditableElement(targetElement)) return;
        
        onMouseDown({
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => e.preventDefault(),
          stopPropagation: () => e.stopPropagation()
        });
      }
    }, { passive: false });
    
    document.addEventListener('touchmove', function(e) {
      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];
        onMouseMove({
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        e.preventDefault();
      }
    }, { passive: false });
    
    document.addEventListener('touchend', onMouseUp);
    document.addEventListener('touchcancel', onMouseUp);
    
    // 存储清理函数到元素上
    el._dragCleanup = function() {
      // 移除鼠标事件
      dragHandle.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      
      // 移除触摸事件
      dragHandle.removeEventListener('touchstart', onMouseDown);
      document.removeEventListener('touchmove', onMouseMove);
      document.removeEventListener('touchend', onMouseUp);
      document.removeEventListener('touchcancel', onMouseUp);
      
      // 恢复手柄样式
      dragHandle.style.cursor = '';
      dragHandle.style.userSelect = '';
    };
    
    // 存储引用以便清理
    el._dragHandlers = {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      dragHandle
    };
  },
  
  // 组件更新时调用
  update(el, binding) {
    const newConfig = binding.value || {};
    const oldConfig = binding.oldValue || {};
    
    // 如果配置发生变化
    if (JSON.stringify(newConfig) !== JSON.stringify(oldConfig)) {
      // 更新禁用状态
      if (newConfig.disabled !== oldConfig.disabled && el._dragHandlers) {
        const { dragHandle } = el._dragHandlers;
        dragHandle.style.cursor = newConfig.disabled ? 'default' : (newConfig.cursor || 'move');
      }
      
      // 更新位置
      if (newConfig.x !== oldConfig.x || newConfig.y !== oldConfig.y) {
        if (newConfig.x !== undefined) {
          el.style.left = newConfig.x + 'px';
        }
        if (newConfig.y !== undefined) {
          el.style.top = newConfig.y + 'px';
        }
      }
      
      // 更新拖拽手柄
      if (newConfig.handle !== oldConfig.handle && el._dragHandlers) {
        // 重新绑定需要重新初始化，这里简单处理
        console.warn('拖拽手柄选择器已更改，需要重新绑定指令');
      }
    }
  },
  
  // 指令解绑时调用
  unbind(el) {
    // 执行清理
    if (el._dragCleanup) {
      el._dragCleanup();
      delete el._dragCleanup;
    }
    
    // 清理引用
    if (el._dragHandlers) {
      delete el._dragHandlers;
    }
    
    // 清理元素样式
    el.style.position = '';
    el.style.left = '';
    el.style.top = '';
    el.style.zIndex = '';
  }
};